// ===========================================
// Initials Avatar Component
// ===========================================
// Displays user initials with role-based brand colors
// Used as fallback when no photo avatar is uploaded
//
// IMPORTANT: This is for USER avatars (Admin, Teacher, Parent).
// Students have a SEPARATE avatar system using character sprites.
// ===========================================

import { Box, Typography, SxProps, Theme } from '@mui/material';
import {
  InitialsAvatarProps,
  generateInitials,
  AVATAR_SIZES,
} from '../../types/avatar.types';
import { useRoleColorScheme } from '../../hooks/useRoleColorScheme';

// ===========================================
// COMPONENT
// ===========================================

export default function InitialsAvatar({
  firstName,
  lastName,
  role,
  size = AVATAR_SIZES.MEDIUM,
  className,
}: InitialsAvatarProps) {
  const initials = generateInitials(firstName, lastName);
  // The school's colour for the role (Settings → Colors, #629).
  const scheme = useRoleColorScheme();
  const colors = scheme[role] ?? scheme.PARENT;

  // Calculate font size based on avatar size
  const fontSize = Math.round(size * 0.4);

  const containerSx: SxProps<Theme> = {
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: colors.background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    userSelect: 'none',
  };

  const textSx: SxProps<Theme> = {
    color: colors.text,
    fontSize: `${fontSize}px`,
    fontWeight: 600,
    lineHeight: 1,
    fontFamily: 'Inter, sans-serif',
  };

  return (
    <Box sx={containerSx} className={className}>
      <Typography sx={textSx} component="span">
        {initials}
      </Typography>
    </Box>
  );
}
